import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Header from "@/components/Header";
import PublicFooter from "@/components/PublicFooter";
import { ArrowRight, Calendar, Clock, User, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback posts in case API fails or returns empty
const fallbackPosts = [
  {
    slug: "how-to-prepare-executive-interview",
    title: "How to Prepare for an Executive Interview: The Complete Playbook",
    excerpt: "Executive interviews require a fundamentally different approach. Learn the strategies that separate successful C-suite candidates from the rest.",
    author: "Hireun Team",
    published_at: "2025-01-01",
    read_time: "12 min read",
    category: "Career Strategy",
    featured: true
  },
  {
    slug: "common-interview-mistakes",
    title: "Common Interview Mistakes Experienced Candidates Still Make",
    excerpt: "Experience doesn't guarantee interview success. Discover the pitfalls that trip up even seasoned professionals.",
    author: "Hireun Team",
    published_at: "2025-01-01",
    read_time: "8 min read",
    category: "Interview Tips",
    featured: false
  },
  {
    slug: "star-method-advanced",
    title: "Beyond Basic STAR: Advanced Techniques for Behavioral Interviews",
    excerpt: "Master the nuances of the STAR method that separate good answers from great ones.",
    author: "Hireun Team",
    published_at: "2025-01-01",
    read_time: "10 min read",
    category: "Interview Tips",
    featured: false
  }
];

const BlogIndexPage = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog/posts`);
        const apiPosts = response.data.posts || [];
        // If API returns posts, use them; otherwise use fallback
        setPosts(apiPosts.length > 0 ? apiPosts : fallbackPosts);
      } catch (error) {
        console.error("Failed to fetch blog posts:", error);
        // Use fallback posts on error
        setPosts(fallbackPosts);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const featuredPost = posts.find(post => post.featured);
  const regularPosts = posts.filter(post => !post.featured);

  const formatDate = (dateStr) => {
    if (!dateStr) return "January 2025";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long"
      });
    } catch {
      return "January 2025";
    }
  };

  return (
    <>
      <Helmet>
        <title>Career Blog - Interview Tips & Career Advice | Hireun</title>
        <meta name="description" content="Expert career advice, interview tips, and job search strategies. Learn from real hiring insights to advance your career." />
        <meta name="keywords" content="career blog, interview tips, job search advice, career advice, professional development" />
        <link rel="canonical" href="https://hireun.com/blog" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />

        {/* Hero Section */}
        <section className="hero-gradient py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Career Blog
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Expert insights on interviews, career development, and job search strategies. 
                Practical advice backed by real hiring experience.
              </p>
            </div>
          </div>
        </section>

        {isLoading ? (
          <section className="py-16">
            <div className="flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          </section>
        ) : (
          <>
            {/* Featured Post */}
            {featuredPost && (
              <section className="py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <h2 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-6">Featured Article</h2>
                  <Link
                    to={`/blog/${featuredPost.slug}`}
                    className="block bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow group"
                  >
                    <div className="md:flex">
                      <div className="md:w-2/5 bg-gradient-to-br from-blue-600 to-indigo-700 p-8 flex items-center justify-center min-h-[200px]">
                        <div className="text-white text-center">
                          <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
                            {featuredPost.category}
                          </span>
                        </div>
                      </div>
                      <div className="md:w-3/5 p-8">
                        <div className="flex items-center text-sm text-gray-500 mb-4">
                          <Calendar className="w-4 h-4 mr-1" />
                          <span>{formatDate(featuredPost.published_at)}</span>
                          <span className="mx-2">•</span>
                          <Clock className="w-4 h-4 mr-1" />
                          <span>{featuredPost.read_time}</span>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                          {featuredPost.title}
                        </h3>
                        <p className="text-gray-600 mb-6">
                          {featuredPost.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm text-gray-500">
                            <User className="w-4 h-4 mr-1" />
                            <span>{featuredPost.author}</span>
                          </div>
                          <span className="flex items-center text-blue-600 font-medium group-hover:translate-x-1 transition-transform">
                            Read Article
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </section>
            )}

            {/* All Posts */}
            <section className="py-12 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-8">Latest Articles</h2>
                {regularPosts.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {regularPosts.map((post) => (
                      <Link
                        key={post.slug}
                        to={`/blog/${post.slug}`}
                        className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow group border border-gray-100"
                      >
                        <span className="text-xs font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                        
                        <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2 group-hover:text-blue-600 transition-colors">
                          {post.title}
                        </h3>
                        
                        <p className="text-gray-600 text-sm mb-4">
                          {post.excerpt}
                        </p>
                        
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            <span>{post.read_time}</span>
                          </div>
                          <span className="flex items-center text-blue-600 font-medium">
                            Read
                            <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    No articles available yet. Check back soon!
                  </p>
                )}
              </div>
            </section>
          </>
        )}

        {/* Newsletter CTA */}
        <section className="py-16 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Get Interview Tips in Your Inbox
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              Join thousands of professionals receiving weekly career insights and interview strategies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <input
                type="email"
                placeholder="Enter your email"
                className="px-4 py-3 rounded-lg text-gray-900 w-full sm:w-80"
              />
              <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                Subscribe
              </button>
            </div>
          </div>
        </section>

        <PublicFooter />
      </div>
    </>
  );
};

export default BlogIndexPage;
